// コネクタ（接続矢印）の幾何計算。
// アンカー / ルーティング / 保存 bbox の算出ロジックは web / api 双方で共用するため domain に移管した。
// ここでは domain のコアを再エクスポートしつつ、描画専用（SVG / Canvas）のヘルパーと、
// コネクタ作成 UI が共有する当たり判定の定数を定義する。
// 経路の編集の追加後は、経路・両端の形状・外接矩形・ハンドルの区間も domain が正本を持つ
// （computeConnectorGeometry）。描画専用のヘルパーは描画面ごとのアダプタ（SVG の d 属性・Canvas2D への描画）で、
// 描画面（キャンバス・作成プレビュー・PDF・サムネイル）は経路を自前で計算せず、必ずこのアダプタを通す。

export {
  CONNECTOR_STUB_PX,
  CONNECTOR_BBOX_PAD_PX,
  sideAnchor,
  sideDirection,
  sideUnitVector,
  frameAABB,
  nearestSide,
  computeConnectorGeometry,
  computeConnectorRoute,
  connectorFrameOf,
  connectorGeometryInputFor,
  connectorStoredFrame,
  connectorStoredFrameForFrames,
  dedupePoints,
  moveConnectorSegment,
  routeBoundingBox,
} from "@refinement-canvas/domain";
export type {
  ConnectorCapGeometry,
  ConnectorFrame,
  ConnectorGeometry,
  ConnectorGeometryInput,
  ConnectorGuideSegment,
  ConnectorPathCommand,
  ConnectorPoint,
} from "@refinement-canvas/domain";

import type {
  ConnectorCapGeometry,
  ConnectorGeometry,
  ConnectorPathCommand,
  ConnectorPoint,
} from "@refinement-canvas/domain";

/**
 * 接続先の当たり判定を少しだけ広げる余白（ステージ座標 px）。
 * コネクタ作成 UI の吸着判定（findConnectTarget）と、コネクタからのオブジェクト自動作成の
 * 抑止判定が同じ値を使うため、双方が依存できる中立なこのモジュールが正本として持つ。
 */
export const CONNECTOR_TARGET_HIT_PAD_PX = 6;

/** 座標を描画面の座標系へ写す関数（平行移動・拡大縮小）。 */
export type ConnectorPointMapper = (point: ConnectorPoint) => ConnectorPoint;

const identity: ConnectorPointMapper = (point) => point;

/** 数値を SVG の属性に書くときの丸め（小数第 3 位まで。表記の揺れで差分を出さない）。 */
function formatNumber(value: number): string {
  const rounded = Math.round(value * 1000) / 1000;
  return Object.is(rounded, -0) ? "0" : String(rounded);
}

function formatPoint(point: ConnectorPoint): string {
  return `${formatNumber(point.x)} ${formatNumber(point.y)}`;
}

/** 描画命令（M / L / Q）を SVG の path の d 属性へ変換する。`map` で座標系を写す（既定は恒等）。 */
export function connectorPathData(
  commands: readonly ConnectorPathCommand[],
  map: ConnectorPointMapper = identity
): string {
  return commands
    .map((command) =>
      command.kind === "Q"
        ? `Q ${formatPoint(map(command.control))} ${formatPoint(map(command.to))}`
        : `${command.kind} ${formatPoint(map(command.to))}`
    )
    .join(" ");
}

/** 点列を SVG の points 属性へ変換する。 */
export function connectorPointsAttribute(
  points: readonly ConnectorPoint[],
  map: ConnectorPointMapper = identity
): string {
  return points
    .map((point) => {
      const mapped = map(point);
      return `${formatNumber(mapped.x)},${formatNumber(mapped.y)}`;
    })
    .join(" ");
}

/** 描画命令の経路を Canvas2D のパスへ写す（beginPath は呼び出し側）。 */
function traceCommands(
  ctx: CanvasRenderingContext2D,
  commands: readonly ConnectorPathCommand[],
  map: ConnectorPointMapper
): void {
  for (const command of commands) {
    const to = map(command.to);
    if (command.kind === "M") ctx.moveTo(to.x, to.y);
    else if (command.kind === "L") ctx.lineTo(to.x, to.y);
    else {
      const control = map(command.control);
      ctx.quadraticCurveTo(control.x, control.y, to.x, to.y);
    }
  }
}

/** Canvas2D へ端の形状を描く（線は線と同じ太さ、塗りは線と同じ色で塗って同じ太さで縁取る）。 */
function drawCap(
  ctx: CanvasRenderingContext2D,
  cap: ConnectorCapGeometry,
  map: ConnectorPointMapper,
  scale: number
): void {
  for (const polygon of cap.fills) {
    ctx.beginPath();
    polygon.forEach((point, index) => {
      const mapped = map(point);
      if (index === 0) ctx.moveTo(mapped.x, mapped.y);
      else ctx.lineTo(mapped.x, mapped.y);
    });
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }
  for (const polyline of cap.strokes) {
    ctx.beginPath();
    polyline.forEach((point, index) => {
      const mapped = map(point);
      if (index === 0) ctx.moveTo(mapped.x, mapped.y);
      else ctx.lineTo(mapped.x, mapped.y);
    });
    ctx.stroke();
  }
  for (const circle of cap.circles) {
    const center = map(circle.center);
    ctx.beginPath();
    ctx.arc(center.x, center.y, Math.max(0, circle.radius * scale), 0, Math.PI * 2);
    // 中空の丸は内側を塗らない（下地を透かす。黒地で穴を偽装しない）。
    if (circle.filled) ctx.fill();
    ctx.stroke();
  }
}

/**
 * Canvas2D（PDF・サムネイル）へコネクタを描く。経路・破線・両端の形状は画面と同じ共有の幾何結果を使い、
 * `map` で出力の座標系へ写し、線幅・丸の半径は `scale` 倍する（線幅の下限は 1px）。
 * `lineWidth` を渡すと、保存線幅によらずその太さ（出力の px）で描く（サムネイルの固定線幅）。
 * 破線の間隔は、実際に描く線幅を基準にした 4w 3w にする（下限や固定の線幅でも比を崩さない）。
 */
export function drawConnectorGeometry(
  ctx: CanvasRenderingContext2D,
  geometry: ConnectorGeometry,
  options: {
    readonly color: string;
    readonly map: ConnectorPointMapper;
    readonly scale: number;
    readonly lineWidth?: number | undefined;
  }
): void {
  const { color, map, scale } = options;
  const lineWidth = options.lineWidth ?? Math.max(1, geometry.strokeWidth * scale);
  ctx.save();
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.lineJoin = "round";
  // 破線は線幅基準の間隔（4w 3w）をそのまま見せるため線端を丸めない。実線は従来どおり丸める。
  ctx.lineCap = geometry.dashArray ? "butt" : "round";
  ctx.setLineDash(
    geometry.dashArray
      ? geometry.dashArray.map((value) => (value / geometry.strokeWidth) * lineWidth)
      : []
  );
  ctx.beginPath();
  traceCommands(ctx, geometry.path, map);
  ctx.stroke();
  // 端の形状は破線にしない。
  ctx.setLineDash([]);
  ctx.lineCap = "round";
  if (geometry.startCap) drawCap(ctx, geometry.startCap, map, scale);
  if (geometry.endCap) drawCap(ctx, geometry.endCap, map, scale);
  ctx.restore();
}
